import { useEffect } from 'react';
import type { ChildMessage, InitPayload, ParentMessage } from '@/types/messaging.types';
import { ROUTES } from '@/utils/constants';

function getAllowedOrigins(): string[] {
  const raw = import.meta.env.VITE_ALLOWED_PARENT_ORIGINS ?? '';
  return raw
    .split(',')
    .map((o) => o.trim())
    .filter(Boolean);
}

function isParentMessage(data: unknown): data is ParentMessage {
  if (typeof data !== 'object' || data === null || !('type' in data)) return false;
  const type = (data as { type: unknown }).type;
  if (type === 'RESET') return true;
  if (type === 'INIT') {
    const payload = (data as { payload?: unknown }).payload;
    return (
      typeof payload === 'object' &&
      payload !== null &&
      'apiKey' in payload &&
      typeof (payload as { apiKey: unknown }).apiKey === 'string'
    );
  }
  return false;
}

export function postToParent(message: ChildMessage): void {
  window.parent.postMessage(message, '*');
}

export function useIframeMessaging(onInit: (payload: InitPayload) => void): void {
  useEffect(() => {
    const allowedOrigins = getAllowedOrigins();

    const handleMessage = (event: MessageEvent): void => {
      if (allowedOrigins.length > 0 && !allowedOrigins.includes(event.origin)) {
        return;
      }
      if (!isParentMessage(event.data)) return;

      if (event.data.type === 'INIT') {
        onInit(event.data.payload);
      } else if (event.data.type === 'RESET') {
        window.location.replace(ROUTES.HOME);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onInit]);
}
